import { useApp } from '@/app/AppContext'
import { Panel } from './record'
import type { TicketTriage } from './supportApi'

/**
 * Разбор обращения ИИ на карточке (#132): категория, тональность, срочность и почему.
 *
 * Не разобрано — сказано словами и с причиной (ИИ выключен, провайдер не ответил, ответ вне
 * справочника), а не пустым местом: пустая панель читалась бы как «всё спокойно». Категория,
 * не совпадающая с видом, который выбрал клиент, показывается обоими словами — расхождение
 * само по себе находка («клиент написал вопрос, а это претензия»), и переписывать выбор
 * клиента сервер не стал.
 *
 * Поля ответа сравниваются нестрого (`== null`): пустых ключей в JSON нет вовсе.
 */
export function TriagePanel({ triage, kind }: { triage?: TicketTriage | null; kind: string }) {
  const { t } = useApp()
  const status = triage?.status
  const hasReading = triage?.category != null && triage.tone != null && triage.urgency != null
  const message = triage?.message ?? ''

  let notice: string | null = null
  if (status == null) {
    notice = t('support.triage.pending')
  } else if (status !== 'analyzed') {
    notice = hasReading ? t('support.triage.stale', { message: message || status }) : t(`support.triage.${status}`, { message })
  }

  return (
    <Panel title={t('support.triage.title')}>
      <div className="stack" style={{ gap: 6 }} data-testid="ticket-triage">
        {notice && (
          <p className={`t-sm ${status === 'rejected' || status === 'failed' ? 'warning' : 'muted'}`} role="note">
            {notice}
          </p>
        )}
        {hasReading && triage && (
          <dl className="stack" style={{ gap: 4, margin: 0 }} aria-label={t('support.triage.title')}>
            <div className="row" style={{ gap: 6 }}>
              <dt className="muted">{t('support.triage.category')}</dt>
              <dd style={{ margin: 0 }}>{t(`value.support.ticket.triage_category.${triage.category}`)}</dd>
            </div>
            <div className="row" style={{ gap: 6 }}>
              <dt className="muted">{t('support.triage.tone')}</dt>
              <dd style={{ margin: 0 }} className={triage.tone === 'harsh' ? 'danger' : undefined}>
                {t(`value.support.ticket.triage_tone.${triage.tone}`)}
              </dd>
            </div>
            <div className="row" style={{ gap: 6 }}>
              <dt className="muted">{t('support.triage.urgency')}</dt>
              <dd style={{ margin: 0 }}>{t(`value.support.ticket.triage_urgency.${triage.urgency}`)}</dd>
            </div>
            {triage.reason != null && triage.reason !== '' && (
              <div className="row" style={{ gap: 6 }}>
                <dt className="muted">{t('support.triage.reason')}</dt>
                <dd style={{ margin: 0 }}>{triage.reason}</dd>
              </div>
            )}
            {triage.routingRuleName != null && (
              <div className="row" style={{ gap: 6 }}>
                <dt className="muted">{t('support.triage.rule')}</dt>
                <dd style={{ margin: 0 }}>{triage.routingRuleName}</dd>
              </div>
            )}
          </dl>
        )}
        {hasReading && triage?.categoryKind != null && triage.categoryKind !== kind && (
          <p className="t-sm" role="note" data-testid="triage-kind-mismatch">
            {t('support.triage.kindMismatch', {
              kind: t(`value.support.ticket.kind.${kind}`),
              category: t(`value.support.ticket.triage_category.${triage.category ?? ''}`),
            })}
          </p>
        )}
      </div>
    </Panel>
  )
}
